import axios from '@/axios'
import router from '@/router'

const state = {
  offerHistory: [],
  orderToView: {},
  isLoading: false,
  error: null
}

const mutations = {
  setOfferHistory(state, payload) {
    state.offerHistory = payload
  },
  setOrderToView(state, payload) {
    state.orderToView = payload
  },
  setLoading(state, payload) {
    state.isLoading = payload
  },
  setError(state, payload) {
    state.error = payload
  }
}

const actions = {
  async fetchOfferHistory({ commit, rootState }) {
    try {
      commit('setLoading', true)
      const { data } = await axios.get(`offer/history`, {
        params: {
          currency: rootState.app.currency
        }
      })
      console.log('Offer history fetched:', data)
      commit('setOfferHistory', data.payload)
      commit('setLoading', false)
      return { success: true }
    } catch (error) {
      commit('setLoading', false)
      commit('setError', error.response?.data?.message || 'Failed to fetch offer history')
      if (error.response?.status === 401) {
        commit('user/setIsAuth', false, { root: true })
      }
      return { success: false, error: error.response?.data?.message || 'Failed to fetch offer history' }
    }
  },

  async createOffer({ commit, dispatch }, data) {
    try {
      commit('setLoading', true)
      const response = await axios.post(`offer/create`, data)
      commit('setLoading', false)

      if (response.data.status === 'OK') {
        commit('setError', null)
        dispatch('modals/closeSaleSkinModal', null, { root: true })
        dispatch('fetchOfferHistory')
        return { success: true }
      } else {
        commit('setError', response.data.message)
        return { success: false, error: response.data.message }
      }
    } catch (error) {
      commit('setLoading', false)

      if (error.response?.data?.errors) {
        const firstErrors = {}
        for (const key in error.response.data.errors) {
          if (
            error.response.data.errors.hasOwnProperty(key) &&
            Array.isArray(error.response.data.errors[key]) &&
            error.response.data.errors[key].length > 0
          ) {
            firstErrors[key] = error.response.data.errors[key][0]
            commit('setError', firstErrors[key])
          }
        }
      } else {
        commit('setError', error.response?.data?.message || 'An error occurred while creating the offer')
      }

      return { success: false, error: error.response?.data?.message || 'An error occurred while creating the offer' }
    }
  },

  async acceptOffer({ commit, dispatch }, offerId) {
    try {
      commit('setLoading', true)
      const response = await axios.post('offer/accept', { id: offerId })
      commit('setLoading', false)

      if (response.data.status === 'OK') {
        commit('setError', null)
        dispatch('modals/closeOfferDetailsModal', null, { root: true })
        dispatch('fetchOfferHistory')

        router.push({ name: 'OfferAcceptedPage' })

        return { success: true }
      } else {
        commit('setError', response.data.message)
        return { success: false, error: response.data.message }
      }
    } catch (error) {
      commit('setLoading', false)
      let errorMesage = null
      if (error.response?.data?.errors) {
        const firstErrors = {}
        for (const key in error.response.data.errors) {
          if (
            error.response.data.errors.hasOwnProperty(key) &&
            Array.isArray(error.response.data.errors[key]) &&
            error.response.data.errors[key].length > 0
          ) {
            firstErrors[key] = error.response.data.errors[key][0]
            errorMesage = error.response.data.errors[key][0]
            commit('setError', firstErrors[key])
          }
        }
      } else {
        errorMesage = error.response?.data?.message
        commit('setError', errorMesage || 'An error occurred while accepting the offer')
      }

      return { success: false, error: errorMesage || 'An error occurred while accepting the offer' }
    }
  },

  async requestPayout({ commit, dispatch }, { data, withdrawMethod }) {
    try {
      commit('setLoading', true)

      const withdrawType = withdrawMethod === 'sepa' ? 'sepa-manual' : 'card-form'
      const response = await axios.post(`offer/request-payout/${withdrawType}`, data)

      commit('setLoading', false)

      if (response.data.status === 'OK') {
        commit('setError', null)
        dispatch('modals/closeAcceptOfferModal', null, { root: true })
        dispatch('modals/closeOfferDetailsModal', null, { root: true })
        dispatch('fetchOfferHistory')

        if (withdrawType === 'card-form' && response.data.redirect_url) {
          window.location.href = response.data.redirect_url
          return { success: true, redirect: true }
        }

        router.push({ name: 'SuccessPayoutPage' })
        return { success: true }
      }

      commit('setError', response.data.message)
      return { success: false, error: response.data.message }
    } catch (error) {
      commit('setLoading', false)

      let errorMesage = null
      if (error.response?.data?.errors) {
        const firstErrors = {}
        for (const key in error.response.data.errors) {
          if (
            error.response.data.errors.hasOwnProperty(key) &&
            Array.isArray(error.response.data.errors[key]) &&
            error.response.data.errors[key].length > 0
          ) {
            firstErrors[key] = error.response.data.errors[key][0]
            errorMesage = error.response.data.errors[key][0]
            commit('setError', firstErrors[key])
          }
        }
      } else {
        errorMesage = error.response?.data?.message
        commit('setError', errorMesage || 'An error occurred while requesting payout')
      }

      return { success: false, error: errorMesage || 'An error occurred while requesting payout' }
    }
  },

  async declineOffer({ commit, dispatch }, id) {
    try {
      commit('setLoading', true)
      const response = await axios.post(`offer/decline`, {
        id
      })
      commit('setLoading', false)

      if (response.data.status === 'OK') {
        commit('setError', null)
        dispatch('modals/closeOfferDetailsModal', null, { root: true })
        dispatch('fetchOfferHistory')
        return { success: true }
      } else {
        commit('setError', response.data.message)
        return { success: false, error: response.data.message }
      }
    } catch (error) {
      commit('setLoading', false)
      commit('setError', error.response?.data?.message || 'An error occurred while declining the offer')
      return { success: false, error: error.response?.data?.message || 'An error occurred while declining the offer' }
    }
  },

  clearError({ commit }) {
    commit('setError', null)
  }
}

const getters = {
  formatDate: () => (timestamp) => {
    if (!timestamp) return ''

    const objectDate = new Date(timestamp * 1000)
    const day = String(objectDate.getDate()).padStart(2, '0')
    const month = String(objectDate.getMonth() + 1).padStart(2, '0')
    const year = objectDate.getFullYear()
    const hours = String(objectDate.getHours()).padStart(2, '0')
    const minutes = String(objectDate.getMinutes()).padStart(2, '0')

    return `${day}.${month}.${year} ${hours}:${minutes}`
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
